/**
 * qrOfficial — pure mapping from a run's schemaless `checks` summary (the audit
 * record's `layer_summary`) to the certificado-QR classification the detail
 * view renders (issue #261, ADR 0006).
 *
 * Since #258 the layer-2 block carries `qr_url` (decoded URL, string|null) and
 * `qr_official_status` (`IN_REGISTRO` | `UNLISTED_GOVERNMENT` | `SUSPECT`,
 * null when no QR/URL). Records sealed before #258 keep their old keys
 * (`qr_validated`) — they, and any malformed shape, must degrade to the absent
 * state, never crash or mislabel.
 */

export const QR_OFFICIAL_STATUSES = [
  "IN_REGISTRO",
  "UNLISTED_GOVERNMENT",
  "SUSPECT",
] as const;

export type QrOfficialStatus = (typeof QR_OFFICIAL_STATUSES)[number];

export type QrSummary = {
  /** The decoded QR URL, or null when no QR / undecodable / no URL. */
  url: string | null;
  /** The registro classification; null degrades to the neutral absent state. */
  status: QrOfficialStatus | null;
};

export type QrBadgeSpec = {
  label: string;
  color: "success" | "warning" | "error";
};

/** Spanish badge per status. SUSPECT mirrors the `QR_DOMINIO_SOSPECHOSO` flag
 * (a HIGH-priority indicador rendered with an error-colored outlined chip). */
export const QR_BADGE: Record<QrOfficialStatus, QrBadgeSpec> = {
  IN_REGISTRO: {
    label: "✓ En el Registro de URLs oficiales",
    color: "success",
  },
  UNLISTED_GOVERNMENT: {
    label: "⚠ Dominio gubernamental/educativo no listado",
    color: "warning",
  },
  SUSPECT: { label: "✗ Dominio sospechoso", color: "error" },
};

/**
 * Whether the decoded QR URL may render as a clickable link (new tab). Only the
 * two registro-vetted classifications qualify: a dominio sospechoso stays plain
 * text (the reviewer eyeballs it, never visits it — ADR 0006), and so does an
 * unclassified URL (`null`), since an unknown future status could be suspect.
 */
export function qrUrlIsLinkable(status: QrOfficialStatus | null): boolean {
  return status === "IN_REGISTRO" || status === "UNLISTED_GOVERNMENT";
}

const ABSENT: QrSummary = { url: null, status: null };

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * Digs `qr_url` / `qr_official_status` out of a run's `checks`.
 *
 * Prefers the top-level `qr` block. The QR read is no longer gated behind Capa 1,
 * so it is present even for expedientes whose Capa 2 never ran — those carry `qr`
 * and NO `layer2`, and reading only `layer2` would show them as having no QR.
 * Falls back to `layer2` for records sealed before the `qr` block existed.
 * An unknown future status is dropped (URL kept) so the UI shows nothing
 * alarming rather than a wrong badge.
 */
export function extractQrSummary(checks: unknown): QrSummary {
  const root = asRecord(checks);
  const source = asRecord(root?.qr) ?? asRecord(root?.layer2);
  if (!source) return ABSENT;
  const rawUrl = source.qr_url;
  const rawStatus = source.qr_official_status;
  return {
    url: typeof rawUrl === "string" && rawUrl.trim() !== "" ? rawUrl : null,
    status: (QR_OFFICIAL_STATUSES as readonly unknown[]).includes(rawStatus)
      ? (rawStatus as QrOfficialStatus)
      : null,
  };
}
